import { brandConfig } from 'brand-config';

/**
 * Generates Coverage A help text based on brand config business rules, ACV/RCE values,
 * and the "Adjust dwelling limit more" toggle state.
 *
 * Used by FO1, FO2, and FO3 risk types:
 * - FO1 and FO2-ACV: ACV-based validation (mobile home per-sqft or percentage of ACV)
 * - FO2-RC: RCE-based validation with year-built-aware minimum (50% for ≤1950, 80% for post-1950)
 * - FO3: RCE-based validation with 90% minimum
 *
 * Issue #28846: Coverage A is now always editable with validation
 * Issue #29003: Added RCE-based brand config support with year-built overrides for FO2 RC
 */
export function getCoverageAHelpText({ item, state, riskType, initialCoverageA }) {
  const propertyType = item?.building?.propertyType;
  const acv = item?.building?.actualCashValueCalculation?.total;
  const rce = item?.building?.replacementCostEstimate?.total;
  const sqFt = item?.building?.sqFt;
  const lossSettlement = item?.coverages?.lossSettlement;
  const canAdjustMore = item.coverages?.canEditCoverageAmount;

  const businessRules = brandConfig.farm?.[state]?.[riskType]?.businessRules?.coverageA;

  // If brand has ACV-based business rules and ACV loss settlement is selected
  if (businessRules && lossSettlement === 'ACV') {
    // Mobile Home: minimum per sq ft, max of ACV or RCE
    if (businessRules.mobileHome?.propertyTypes?.includes(propertyType) && sqFt && acv) {
      const minValue = sqFt * businessRules.mobileHome.minPerSqFt;
      const maxValue = Math.max(acv, rce || 0);
      if (canAdjustMore) {
        return `Minimum: $${minValue.toLocaleString()} (based on ${sqFt} sq ft at $${businessRules.mobileHome.minPerSqFt}/sq ft). No maximum with justification.`;
      }
      return `You may adjust this value from $${minValue.toLocaleString()} (based on ${sqFt} sq ft at $${businessRules.mobileHome.minPerSqFt}/sq ft) to $${maxValue.toLocaleString()} (greater of ACV or RCE)`;
    }
    // Home/Modular/Barndominium: percentage of ACV, max of ACV or RCE
    if (businessRules.acvBasedStructures?.propertyTypes?.includes(propertyType) && acv) {
      const minValue = Math.round(acv * businessRules.acvBasedStructures.minPercent);
      const maxValue = Math.max(acv, rce || 0);
      if (canAdjustMore) {
        return `Minimum: $${minValue.toLocaleString()} (${businessRules.acvBasedStructures.minPercent * 100}% ACV). No maximum with justification.`;
      }
      return `You may adjust this value from $${minValue.toLocaleString()} (${businessRules.acvBasedStructures.minPercent * 100}% ACV) to $${maxValue.toLocaleString()} (greater of ACV or RCE)`;
    }
  }

  // RCE-based help text: use brand config rceBasedStructures if available (GitHub issue #29003)
  if (businessRules?.rceBasedStructures && rce && lossSettlement !== 'ACV') {
    const rceRules = businessRules.rceBasedStructures;
    const yearBuilt = item?.building?.yearBuilt;
    let minPercent = rceRules.minPercent;

    // Check year-built overrides (e.g., 50% for homes built 1950 or before)
    if (rceRules.yearBuiltOverrides && yearBuilt && yearBuilt <= rceRules.yearBuiltOverrides.threshold) {
      minPercent = rceRules.yearBuiltOverrides.minPercentAtOrBefore;
    }

    const minValue = Math.round(rce * minPercent);
    const maxValue = Math.round(rce * rceRules.maxPercent);
    if (canAdjustMore) {
      return `Minimum: $${minValue.toLocaleString()} (${minPercent * 100}% RCE). No maximum with justification.`;
    }
    return `You may adjust this from $${minValue.toLocaleString()} (${minPercent * 100}% RCE) to $${maxValue.toLocaleString()} (${rceRules.maxPercent * 100}% RCE).`;
  }

  // Default fallback for brands without brand config rules
  if (canAdjustMore) {
    return `Minimum: $${Math.round(initialCoverageA * 0.8).toLocaleString()} (80% RCE). No maximum with justification.`;
  }
  return `You may adjust this from $${Math.round(initialCoverageA * 0.8).toLocaleString()} (80% RCE) to $${initialCoverageA.toLocaleString()} (100% RCE).`;
}
